import { useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { toast } from "sonner";
import { Mail, Link2, Copy, X, Check, Users } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

export default function ShareBoardDialog({ boardId, open, onOpenChange }) {
  const qc = useQueryClient();
  const [emailsText, setEmailsText] = useState("");
  const [copied, setCopied] = useState(false);

  const { data: info, refetch } = useQuery({
    queryKey: ["board-share", boardId],
    queryFn: async () => (await api.get(`/boards/${boardId}/share`)).data,
    enabled: open,
  });

  const invite = useMutation({
    mutationFn: async (emails) => (await api.post(`/boards/${boardId}/share/emails`, { emails })).data,
    onSuccess: (res) => {
      toast.success(`${res.invited.length} invitación(es) enviadas`);
      setEmailsText("");
      refetch();
      qc.invalidateQueries({ queryKey: ["board-full", boardId] });
      qc.invalidateQueries({ queryKey: ["recent-boards"] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "No se pudo invitar"),
  });

  const toggleLink = useMutation({
    mutationFn: async (enabled) => (await api.post(`/boards/${boardId}/share/link`, { enabled })).data,
    onSuccess: () => refetch(),
    onError: () => toast.error("No se pudo actualizar el enlace"),
  });

  const removeMember = useMutation({
    mutationFn: async (member_id) => (await api.delete(`/boards/${boardId}/members/${member_id}`)).data,
    onSuccess: () => { refetch(); qc.invalidateQueries({ queryKey: ["board-full", boardId] }); toast.success("Miembro eliminado"); },
    onError: () => toast.error("No se pudo eliminar"),
  });

  const sendInvites = () => {
    const emails = emailsText.split(/[\s,;]+/).map(e => e.trim()).filter(Boolean);
    if (!emails.length) return toast.error("Agrega al menos un email");
    // Basic validation
    const invalid = emails.filter(e => !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e));
    if (invalid.length) return toast.error(`Email inválido: ${invalid[0]}`);
    invite.mutate(emails);
  };

  const shareUrl = info?.share_link
    ? `${window.location.origin}/share/${info.share_link.token}`
    : "";

  const copyLink = async () => {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success("Enlace copiado");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("No se pudo copiar");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="glass-panel border-white/40 dark:border-white/10 sm:max-w-lg rounded-2xl">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <Users className="h-5 w-5" /> Compartir board
          </DialogTitle>
          <DialogDescription>Invita a otros usuarios por email o con un enlace.</DialogDescription>
        </DialogHeader>

        {/* Email invite */}
        <div className="space-y-2">
          <label className="text-xs uppercase tracking-wider text-muted-foreground font-semibold inline-flex items-center gap-1.5">
            <Mail className="h-3.5 w-3.5" /> Invitar por email
          </label>
          <Textarea
            value={emailsText}
            onChange={(e) => setEmailsText(e.target.value)}
            placeholder="ana@equipo.com, luis@equipo.com"
            rows={2}
            className="bg-white/60 dark:bg-slate-900/40"
            data-testid="share-emails-input"
          />
          <Button
            size="sm"
            onClick={sendInvites}
            disabled={invite.isPending || !emailsText.trim()}
            className="bg-blue-600 hover:bg-blue-700 text-white"
            data-testid="share-emails-submit"
          >
            {invite.isPending ? "Enviando…" : "Enviar invitación"}
          </Button>
        </div>

        {/* Share link — disponible para cualquier miembro */}
        <div className="space-y-2 border-t border-white/30 dark:border-white/10 pt-4">
          <div className="flex items-center justify-between">
            <label className="text-xs uppercase tracking-wider text-muted-foreground font-semibold inline-flex items-center gap-1.5">
              <Link2 className="h-3.5 w-3.5" /> Enlace de invitación
            </label>
            <Switch
              checked={!!info?.share_link}
              onCheckedChange={(checked) => toggleLink.mutate(checked)}
              data-testid="share-link-toggle"
            />
          </div>
          {info?.share_link && (
            <div className="flex items-center gap-2">
              <Input
                readOnly
                value={shareUrl}
                onFocus={(e) => e.target.select()}
                className="bg-white/60 dark:bg-slate-900/40 font-mono text-xs"
                data-testid="share-link-input"
              />
              <Button size="sm" variant="outline" onClick={copyLink} data-testid="share-link-copy">
                {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              </Button>
            </div>
          )}
          <p className="text-[11px] text-muted-foreground">
            Cualquiera con este enlace podrá unirse como editor del board.
          </p>
        </div>

        {/* Members list */}
        <div className="space-y-2 border-t border-white/30 dark:border-white/10 pt-4">
          <label className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Miembros invitados</label>
          {(info?.members || []).length === 0 && (
            <p className="text-xs text-muted-foreground">Aún no has invitado a nadie.</p>
          )}
          <div className="space-y-1 max-h-48 overflow-y-auto kanban-scroll">
            {info?.members?.map((m) => (
              <div key={m.member_id} className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-white/40 dark:hover:bg-white/10" data-testid={`share-member-${m.member_id}`}>
                <Avatar className="h-7 w-7">
                  <AvatarImage src={m.user?.picture} alt={m.user?.name} />
                  <AvatarFallback>{(m.user?.name || m.invited_email)[0].toUpperCase()}</AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{m.user?.name || m.invited_email}</p>
                  <p className="text-xs text-muted-foreground truncate">
                    {m.user ? m.user.email : "Pendiente"}
                    <span className="ml-1 text-[10px] uppercase">· {m.role}</span>
                  </p>
                </div>
                {info?.is_owner && (
                  <button
                    onClick={() => removeMember.mutate(m.member_id)}
                    className="h-7 w-7 grid place-items-center rounded-md text-rose-500 hover:bg-rose-500/10"
                    data-testid={`share-member-remove-${m.member_id}`}
                    aria-label="Eliminar miembro"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="share-close">Cerrar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
